import React, {useEffect, useContext, createContext, useState} from 'react';

import {GoogleTranslate} from './net/translate';
import {useAppConfig} from './net/query/AppConfig.gql';
import {useToast} from './components/ui/toast';

/**
 * Context to store Google Translate API client.
 */
const GoogleTranslateApiContext = createContext<GoogleTranslate | null>(null);

/**
 * Get the Translate API client from the context.
 */
export const useGoogleTranslateApi = () => {
  return useContext(GoogleTranslateApiContext);
};

export const GoogleTranslateApiProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [gt, setGt] = useState<GoogleTranslate | null>(null);
  const {data: appConfig, error} = useAppConfig();
  const {showToast} = useToast();
  const apiKey = appConfig?.clientConfig?.googleTranslateKey;

  useEffect(() => {
    setGt(apiKey ? new GoogleTranslate(apiKey) : null);
  }, [apiKey]);

  useEffect(() => {
    if (error) {
      showToast(`Error loading app config: ${error}`, 'error');
    }
  }, [error, showToast]);

  return (
    <GoogleTranslateApiContext.Provider value={gt}>
      {children}
    </GoogleTranslateApiContext.Provider>
  );
};

/**
 * Translation hook arguments.
 */
export type UseTranslationProps = {
  text: string;
  skip?: boolean;
  into?: string;
  source?: string;
};

export const useTranslation = ({
  text,
  skip,
  into,
  source,
}: UseTranslationProps) => {
  const [translation, setTranslation] = useState<string | null>(null);
  const [lang, setLang] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [canTranslate, setCanTranslate] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const api = useGoogleTranslateApi();

  useEffect(() => {
    setLoading(false);
    if (!api) {
      setCanTranslate(false);
      setLang(null);
      setTranslation(null);
      return;
    }

    setCanTranslate(true);

    if (skip) {
      return;
    }

    const toLang = into || 'en';
    if (toLang === source) {
      setTranslation(text);
      setLang(source);
      setCanTranslate(false);
      return;
    }

    setLoading(true);
    setError(null);
    api
      .translate(text, toLang, source)
      .then((r) => {
        const translations = r.data.translations;
        if (translations.length === 0) {
          throw new Error('no translation found');
        }
        const t = translations[0]!;
        setTranslation(t.translatedText);
        setLang(t.detectedSourceLanguage);
        setCanTranslate(false);
      })
      .catch((e) => {
        setError(e);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [api, text, skip, into, source]);

  return {
    translation,
    fromLang: lang,
    translating: loading,
    canTranslate,
    error,
  };
};

/**
 * Supported languages.
 *
 * These come from a list of the largest languages spoken in the Bay Area, plus
 * a list of languages that we have seen local requests for translators.
 *
 * We can add any language that Google supports, the full list is here:
 * https://cloud.google.com/translate/docs/languages
 *
 * Note that some relatively common languages that are requested are not
 * supported by Google, like Cantonese.
 */
export const Languages = [
  {code: 'en', name: 'English', alt: []},
  {code: 'es', name: 'Spanish', alt: ['Spanish/Español']},
  {code: 'vi', name: 'Vietnamese', alt: []},
  {code: 'ru', name: 'Russian', alt: []},
  {code: 'zh', name: 'Chinese', alt: ['Mandarin']},
  {code: 'fr', name: 'French', alt: []},
  {code: 'pl', name: 'Polish', alt: []},
  {code: 'tl', name: 'Tagalog', alt: ['Filipino']},
  {code: 'ja', name: 'Japanese', alt: []},
  {code: 'ko', name: 'Korean', alt: []},
  {code: 'de', name: 'German', alt: []},
  {code: 'pt', name: 'Portuguese', alt: []},
  {code: 'fa', name: 'Persian', alt: ['Farsi']},
  {code: 'pa', name: 'Punjabi', alt: []},
  {code: 'ro', name: 'Romanian', alt: []},
].sort((a, b) => (a.name < b.name ? -1 : 1));

/**
 * Map to look up supported language codes by name.
 */
const langLookup = Languages.reduce(
  (agg, cur) => {
    const names = [cur.name, ...cur.alt];
    for (const name of names) {
      agg[name.toLowerCase()] = cur.code;
    }
    return agg;
  },
  {} as {[key: string]: string},
);

/**
 * Map to look up supported language names by code.
 */
const codeLookup = Languages.reduce(
  (agg, cur) => {
    agg[cur.code] = cur.name;
    return agg;
  },
  {} as {[key: string]: string},
);

/**
 * Try to get language code from language name.
 */
export const normLanguage = (lang: string) => {
  const needle = lang.toLowerCase();
  if (Object.hasOwn(langLookup, needle)) {
    return langLookup[needle];
  }

  return needle;
};

/**
 * Try to get the full name of a language by its code.
 */
export const lookupLanguageName = (code: string) => {
  const needle = code.toLowerCase();
  if (Object.hasOwn(codeLookup, needle)) {
    return codeLookup[needle];
  }
  return code;
};
